"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect, useMemo } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Cpu, ArrowUpRight, Wrench, Sparkles, Funnel as Filter, Layers, Calendar, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
type Language = "vi" | "en";
const DEFAULT_MASCOT_SRC = "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/c2386fca-8209-4748-ae37-47d275ce8104.png";
const SECTION_CONTENT = {
  specRail: {
    vi: "Bản Thiết Kế Lab // Hồ Sơ Dự Án",
    en: "Lab Blueprint // Build Archive"
  },
  heading: {
    vi: "Kỹ thuật thực chiến, sáng chế bởi học viên",
    en: "Real engineering, built by students"
  },
  subtitle: {
    vi: "Từ robot tự hành vượt mê cung đến cánh tay giả sinh học, khám phá những sáng chế đột phá do học viên trực tiếp thiết kế và lập trình tại phòng lab ROBOTO.",
    en: "From autonomous rovers to robotic prosthetics, see what young innovators design and program in our labs."
  },
  lead: {
    vi: "Tất cả dự án đều do học viên từ 4 đến 16 tuổi tự tay lên ý tưởng, đấu nối mạch phần cứng và lập trình Coding. Tỉ lệ kèm cặp sát sao của giảng viên giúp từng em làm chủ cơ cấu cơ khí chuyên sâu và hiện thực hóa tư duy thuật toán trên thiết bị phần cứng thật.",
    en: "Every build is conceived, wired, and coded by students aged 4 to 16. Our low mentor ratio gives each builder the tools and guidance to translate complex mechanics and algorithmic logic into physical hardware."
  },
  featuredBadge: {
    vi: "Dự Án Tiêu Biểu Tuyển Chọn",
    en: "Flagship Lab Showcase"
  },
  featuredAction: {
    vi: "Khám Phá Dự Án Chi Tiết",
    en: "View Featured Build"
  },
  filterAge: {
    vi: "Độ tuổi",
    en: "Age Group"
  },
  filterDomain: {
    vi: "Lĩnh vực",
    en: "Field Domain"
  },
  allFilter: {
    vi: "Tất cả",
    en: "All"
  },
  verifiedBadge: {
    vi: "Dự Án Lab Đạt Chuẩn",
    en: "Verified Lab Build"
  },
  specLink: {
    vi: "Chi Tiết Kỹ Thuật",
    en: "Specification"
  },
  relatedProgram: {
    vi: "Chương trình liên quan",
    en: "Related program"
  },
  engineeringProcess: {
    vi: "Quy trình kỹ thuật",
    en: "Engineering Process"
  },
  noResults: {
    vi: "Không tìm thấy dự án phù hợp với bộ lọc đã chọn.",
    en: "No projects match the selected filters."
  },
  resetFilters: {
    vi: "Đặt lại bộ lọc",
    en: "Reset filters"
  },
  loading: {
    vi: "Đang tải hồ sơ dự án...",
    en: "Loading project archive..."
  },
  loadError: {
    vi: "Không thể tải danh sách dự án. Vui lòng thử lại sau.",
    en: "Unable to load projects. Please try again later."
  },
  ctaHeading: {
    vi: "Ba mẹ muốn trực tiếp chứng kiến các em học và sáng chế?",
    en: "Want to see our students live in action?"
  },
  ctaDescription: {
    vi: "Đăng ký buổi học thử miễn phí để tham quan không gian lab tiêu chuẩn, gặp gỡ đội ngũ giảng viên và xem trực tiếp buổi trình diễn Robotics thực tế.",
    en: "Book a free trial class to inspect the workspace, meet our instructors, and watch a robotics demonstration."
  },
  ctaTrialBtn: {
    vi: "Đăng Ký Học Thử Miễn Phí",
    en: "Book a Free Trial"
  },
  ctaProgramsBtn: {
    vi: "Khám Phá Các Chương Trình",
    en: "Explore Programs"
  }
};
const PROCESS_STEP_LABELS: Record<string, {
  vi: string;
  en: string;
}> = {
  challenge: {
    vi: "Bài toán",
    en: "Challenge"
  },
  idea: {
    vi: "Ý tưởng",
    en: "Idea"
  },
  design: {
    vi: "Thiết kế",
    en: "Design"
  },
  programming: {
    vi: "Lập trình",
    en: "Programming"
  },
  testing: {
    vi: "Thử nghiệm",
    en: "Testing"
  },
  improvement: {
    vi: "Cải tiến",
    en: "Improvement"
  },
  result: {
    vi: "Kết quả",
    en: "Result"
  }
};
interface ProjectsGalleryConsumerProps {
  lang: Language;
  programs: any[];
}
function ProjectsGalleryConsumer({
  lang,
  programs
}: ProjectsGalleryConsumerProps) {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  const shouldReduceMotion = useReducedMotion();
  const [selectedAge, setSelectedAge] = useState<string>("all");
  const [selectedDomain, setSelectedDomain] = useState<string>("all");

  // Select first project as featured hero build
  const featuredProject = useMemo(() => {
    return posts && posts.length > 0 ? posts[0] : null;
  }, [posts]);

  // Exclude featured project from remaining grid archive
  const remainingProjects = useMemo(() => {
    return posts && posts.length > 1 ? posts.slice(1) : [];
  }, [posts]);

  // Derive distinct age ranges and field domains from live posts
  const {
    ageOptions,
    domainOptions
  } = useMemo(() => {
    const ages = new Set<string>();
    const domains = new Set<string>();
    posts.forEach((post: any) => {
      const age = post?.acf?.age_range || post?.customFields?.age_range;
      const domain = post?.acf?.field_domain || post?.customFields?.field_domain;
      if (typeof age === "string" && age.trim()) {
        ages.add(age.trim());
      }
      if (typeof domain === "string" && domain.trim()) {
        domains.add(domain.trim());
      }
    });
    return {
      ageOptions: Array.from(ages).sort(),
      domainOptions: Array.from(domains).sort()
    };
  }, [posts]);

  // Client-side filtering with AND logic applied to remaining projects
  const filteredProjects = useMemo(() => {
    return remainingProjects.filter((post: any) => {
      const age = post?.acf?.age_range || post?.customFields?.age_range;
      const domain = post?.acf?.field_domain || post?.customFields?.field_domain;
      const matchesAge = selectedAge === "all" || age === selectedAge;
      const matchesDomain = selectedDomain === "all" || domain === selectedDomain;
      return matchesAge && matchesDomain;
    });
  }, [remainingProjects, selectedAge, selectedDomain]);

  // Helper function to match a program by project_domain_link === field_domain
  const getMatchedProgram = (projectDomain?: string) => {
    if (!projectDomain || !programs || programs.length === 0) return null;
    const cleanDomain = String(projectDomain).trim().toLowerCase();
    return programs.find((prog: any) => {
      const progDomain = prog?.acf?.project_domain_link || prog?.customFields?.project_domain_link;
      return typeof progDomain === "string" && progDomain.trim().toLowerCase() === cleanDomain;
    });
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.45
      }
    }
  };
  if (loading) {
    return <div className="space-y-8">
        <div className="h-80 w-full rounded-2xl bg-muted animate-pulse" />
        <div className="flex gap-4 animate-pulse">
          <div className="h-10 w-32 rounded-lg bg-muted" />
          <div className="h-10 w-32 rounded-lg bg-muted" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {[...Array(6)].map((_, i) => <div key={i} className={`rounded-xl border border-border bg-card p-6 h-96 animate-pulse flex flex-col justify-between ${i === 0 || i === 3 ? "lg:col-span-2" : ""}`}>
              <div className="w-full h-48 rounded-lg bg-muted" />
              <div className="space-y-3 mt-4">
                <div className="h-5 w-3/4 rounded-sm bg-muted" />
                <div className="h-4 w-full rounded-sm bg-muted/60" />
                <div className="h-4 w-2/3 rounded-sm bg-muted/60" />
              </div>
            </div>)}
        </div>
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-destructive/30 bg-destructive/5 p-8 text-center">
        <p className="text-foreground font-sans">{SECTION_CONTENT.loadError[lang]}</p>
        <Button onClick={refetch} variant="outline" className="mt-4 border-border hover:bg-card">
          {lang === "vi" ? "Tải lại" : "Retry"}
        </Button>
      </div>;
  }
  return <div className="space-y-10 md:space-y-12">
      {/* Large Featured Project Hero Block */}
      {featuredProject && (() => {
      const fallbackTitle = lang === "vi" ? "Dự Án Lab Nổi Bật" : "Featured Lab Project";
      const featName = featuredProject?.acf?.project_name || featuredProject?.customFields?.project_name || featuredProject?.title || fallbackTitle;
      const featImgUrl = featuredProject?.acf?.image || featuredProject?.customFields?.image || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/autonomous_maze_rover_build.png";
      const featTech = featuredProject?.acf?.technology || featuredProject?.customFields?.technology;
      const featDomain = featuredProject?.acf?.field_domain || featuredProject?.customFields?.field_domain;
      const featDesc = featuredProject?.acf?.short_description || featuredProject?.customFields?.short_description || "";
      const featAge = featuredProject?.acf?.age_range || featuredProject?.customFields?.age_range;
      const featLink = featuredProject?.link || "#";
      const featImageObj = {
        src: featImgUrl,
        srcset: "",
        sizes: ""
      };
      const matchedFeatProgram = getMatchedProgram(featDomain);
      return <div className="relative rounded-2xl border-2 border-primary/25 bg-card overflow-hidden shadow-lg">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-0">
              {/* Featured Visual Media */}
              <div className="lg:col-span-7 relative min-h-[320px] lg:min-h-[460px] bg-muted overflow-hidden">
                {featImageObj.src && <img src={featImageObj.src} data-wvc-srcset={featImageObj.srcset} data-wvc-sizes={featImageObj.sizes} alt={featName} loading="lazy" decoding="async" className="h-full w-full object-cover object-center transition-transform duration-700 hover:scale-105" />}
                <div className="absolute inset-0 bg-gradient-to-t lg:bg-gradient-to-r from-primary/85 via-primary/30 to-transparent pointer-events-none" />

                {/* Floating Badges */}
                <div className="absolute top-4 left-4 flex flex-wrap items-center gap-2">
                  <span className="inline-flex items-center gap-1.5 font-mono text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg bg-accent text-white shadow-xs">
                    <Sparkles className="size-3.5" />
                    {SECTION_CONTENT.featuredBadge[lang]}
                  </span>
                  {featAge && <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded-lg bg-background/90 text-foreground backdrop-blur-xs border border-border shadow-xs">
                      {featAge}
                    </span>}
                </div>

                {/* Bottom Overlay Info on Media */}
                <div className="absolute bottom-4 left-4 right-4 flex flex-wrap items-center gap-2 pointer-events-none">
                  {featDomain && <span className="inline-flex items-center gap-1.5 text-xs text-white font-medium px-3 py-1 rounded-md bg-primary/80 backdrop-blur-xs border border-white/20">
                      <Cpu className="size-3.5 text-secondary" />
                      <span>{featDomain}</span>
                    </span>}
                  {featTech && <span className="inline-flex items-center text-xs text-white/95 font-medium px-3 py-1 rounded-md bg-foreground/75 backdrop-blur-xs border border-white/20">
                      {featTech}
                    </span>}
                </div>
              </div>

              {/* Featured Details Column */}
              <div className="lg:col-span-5 p-6 sm:p-8 lg:p-10 flex flex-col justify-between bg-card">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-wider text-secondary">
                    <span className="size-2 rounded-xs bg-secondary" />
                    <span>01 // {featDomain || "ROBOTICS"}</span>
                  </div>

                  <h3 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground leading-tight">
                    <Link to={featLink} className="hover:text-primary transition-colors">
                      {featName}
                    </Link>
                  </h3>

                  {featDesc && <p className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {featDesc}
                    </p>}

                  {matchedFeatProgram?.link && <div className="pt-2">
                      <Link to={matchedFeatProgram.link} className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-semibold text-secondary hover:underline">
                        <Sparkles className="size-3.5 text-secondary" />
                        <span>{SECTION_CONTENT.relatedProgram[lang]}:</span>
                        <span className="text-foreground hover:text-primary">
                          {matchedFeatProgram.title}
                        </span>
                        <ChevronRight className="size-3.5" />
                      </Link>
                    </div>}
                </div>

                <div className="mt-8 pt-6 border-t border-border flex flex-wrap items-center justify-between gap-4">
                  <div className="flex items-center gap-2 font-mono text-xs text-muted-foreground">
                    <Wrench className="size-3.5 text-secondary" />
                    <span>{SECTION_CONTENT.verifiedBadge[lang]}</span>
                  </div>

                  <Button asChild className="w-full sm:w-auto bg-accent text-white hover:bg-accent/90 shadow-md font-sans font-semibold px-6 py-3 h-auto">
                    <Link to={featLink} className="inline-flex items-center gap-2">
                      <span>{SECTION_CONTENT.featuredAction[lang]}</span>
                      <ArrowUpRight className="size-4" />
                    </Link>
                  </Button>
                </div>
              </div>
            </div>
          </div>;
    })()}

      {/* Filter Control Bar */}
      <div className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-xs space-y-4">
        <div className="flex items-center gap-2 text-xs font-mono font-semibold uppercase tracking-wider text-muted-foreground pb-2 border-b border-border/70">
          <Filter className="h-3.5 w-3.5 text-secondary" />
          <span>{lang === "vi" ? "Bộ Lọc Dự Án" : "Project Filter Controls"}</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {/* Group 1: Age Filter */}
          <div className="space-y-2">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground font-sans">
              <SafeHydrate><Calendar className="h-3.5 w-3.5 text-secondary" /></SafeHydrate>
              <span>{SECTION_CONTENT.filterAge[lang]}:</span>
            </label>
            <div className="flex flex-wrap items-center gap-1.5">
              <Button type="button" size="sm" onClick={() => setSelectedAge("all")} variant={selectedAge === "all" ? "default" : "outline"} className={`h-8 px-3 text-xs font-medium rounded-lg transition-colors ${selectedAge === "all" ? "bg-primary text-primary-foreground shadow-2xs hover:bg-primary/90" : "bg-muted text-foreground/80 hover:bg-muted/80 border-border/60"}`}>
                {SECTION_CONTENT.allFilter[lang]}
              </Button>
              {ageOptions.map(age => <Button key={age} type="button" size="sm" onClick={() => setSelectedAge(age)} variant={selectedAge === age ? "default" : "outline"} className={`h-8 px-3 text-xs font-medium rounded-lg transition-colors ${selectedAge === age ? "bg-primary text-primary-foreground shadow-2xs hover:bg-primary/90" : "bg-muted text-foreground/80 hover:bg-muted/80 border-border/60"}`}>
                  {age}
                </Button>)}
            </div>
          </div>

          {/* Group 2: Field Domain Filter */}
          <div className="space-y-2">
            <label className="flex items-center gap-1.5 text-xs font-medium text-foreground font-sans">
              <Layers className="h-3.5 w-3.5 text-secondary" />
              <span>{SECTION_CONTENT.filterDomain[lang]}:</span>
            </label>
            <div className="flex flex-wrap items-center gap-1.5">
              <Button type="button" size="sm" onClick={() => setSelectedDomain("all")} variant={selectedDomain === "all" ? "secondary" : "outline"} className={`h-8 px-3 text-xs font-medium rounded-lg transition-colors ${selectedDomain === "all" ? "bg-secondary text-secondary-foreground shadow-2xs font-semibold hover:bg-secondary/90" : "bg-muted text-foreground/80 hover:bg-muted/80 border-border/60"}`}>
                {SECTION_CONTENT.allFilter[lang]}
              </Button>
              {domainOptions.map(domain => <Button key={domain} type="button" size="sm" onClick={() => setSelectedDomain(domain)} variant={selectedDomain === domain ? "secondary" : "outline"} className={`h-8 px-3 text-xs font-medium rounded-lg transition-colors ${selectedDomain === domain ? "bg-secondary text-secondary-foreground shadow-2xs font-semibold hover:bg-secondary/90" : "bg-muted text-foreground/80 hover:bg-muted/80 border-border/60"}`}>
                  {domain}
                </Button>)}
            </div>
          </div>
        </div>
      </div>

      {/* Editorial Masonry-Style Grid with Process Hover-Reveal */}
      {filteredProjects.length === 0 ? <div className="rounded-xl border border-dashed border-border p-12 text-center bg-card/50">
          <p className="text-muted-foreground font-sans text-sm md:text-base">
            {SECTION_CONTENT.noResults[lang]}
          </p>
          {(selectedAge !== "all" || selectedDomain !== "all") && <Button onClick={() => {
        setSelectedAge("all");
        setSelectedDomain("all");
      }} variant="outline" size="sm" className="mt-4 border-border hover:bg-card">
              {SECTION_CONTENT.resetFilters[lang]}
            </Button>}
        </div> : <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
      once: true,
      margin: "-50px"
    }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {filteredProjects.map((post: any, index: number) => {
        const projectName = post?.acf?.project_name || post?.customFields?.project_name || "Project Build";
        const imageUrl = post?.acf?.image || post?.customFields?.image || "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1806685/img/autonomous_maze_rover_build.png";
        const ageRange = post?.acf?.age_range || post?.customFields?.age_range;
        const fieldDomain = post?.acf?.field_domain || post?.customFields?.field_domain;
        const technology = post?.acf?.technology || post?.customFields?.technology;
        const shortDesc = post?.acf?.short_description || post?.customFields?.short_description || "";
        const projectLink = post?.link || "#";

        // Extract engineering process fields when non-empty
        const processSteps = [{
          key: "challenge",
          label: PROCESS_STEP_LABELS.challenge[lang],
          value: post?.acf?.challenge || post?.customFields?.challenge
        }, {
          key: "idea",
          label: PROCESS_STEP_LABELS.idea[lang],
          value: post?.acf?.idea || post?.customFields?.idea
        }, {
          key: "design",
          label: PROCESS_STEP_LABELS.design[lang],
          value: post?.acf?.design || post?.customFields?.design
        }, {
          key: "programming",
          label: PROCESS_STEP_LABELS.programming[lang],
          value: post?.acf?.programming || post?.customFields?.programming
        }, {
          key: "testing",
          label: PROCESS_STEP_LABELS.testing[lang],
          value: post?.acf?.testing || post?.customFields?.testing
        }, {
          key: "improvement",
          label: PROCESS_STEP_LABELS.improvement[lang],
          value: post?.acf?.improvement || post?.customFields?.improvement
        }, {
          key: "result",
          label: PROCESS_STEP_LABELS.result[lang],
          value: post?.acf?.result || post?.customFields?.result
        }].filter(step => typeof step.value === "string" && step.value.trim().length > 0);

        // Match program via field_domain
        const matchedProgram = getMatchedProgram(fieldDomain);
        const imageObject = {
          src: imageUrl,
          srcset: "",
          sizes: ""
        };
        const isFeaturedCard = index % 5 === 0 || index % 5 === 3;
        const isTallCard = index % 7 === 5;
        return <motion.div key={post.id || `proj-${index}`} data-index={index} variants={cardVariants} whileHover={shouldReduceMotion ? {} : {
          y: -4
        }} className={`h-full flex min-w-0 ${isFeaturedCard ? "lg:col-span-2" : "col-span-1"}`}>
                <Card className={`group relative flex flex-col justify-between w-full min-w-0 overflow-hidden border border-border bg-card rounded-xl transition-[border-color,box-shadow] duration-200 hover:border-secondary hover:shadow-lg ${isFeaturedCard ? "hover:ring-1 hover:ring-secondary/30" : ""}`}>
                  {/* Visual Header / Media with Process Hover Overlay */}
                  <div className={`relative w-full overflow-hidden bg-muted ${isFeaturedCard ? "aspect-[16/9]" : isTallCard ? "aspect-[3/4]" : "aspect-[16/10]"}`}>
                    {imageObject.src && <img src={imageObject.src} data-wvc-srcset={imageObject.srcset} data-wvc-sizes={imageObject.sizes} alt={projectName} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" />}

                    {/* Default Media Scrim */}
                    <div className="absolute inset-0 bg-gradient-to-t from-primary/75 via-primary/20 to-transparent opacity-85 pointer-events-none" />

                    {/* Top Chips */}
                    <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none z-10">
                      {ageRange && <span className="font-mono text-xs font-semibold px-2.5 py-1 rounded-lg bg-card/90 text-foreground backdrop-blur-xs border border-border shadow-xs">
                          {ageRange}
                        </span>}
                      <span className="font-mono text-xs font-medium px-2 py-0.5 rounded-md bg-primary text-primary-foreground ml-auto">
                        {String(index + 2).padStart(2, "0")}
                      </span>
                    </div>

                    {/* Bottom Metadata Chips */}
                    <div className="absolute bottom-3 left-3 right-3 flex flex-wrap items-center gap-1.5 pointer-events-none z-10">
                      {fieldDomain && <span className="inline-flex items-center gap-1 text-xs text-primary-foreground font-medium px-2 py-1 rounded-md bg-primary/75 backdrop-blur-xs border border-white/10">
                          <span className="p-0.5 rounded-sm bg-secondary/30">
                            <Cpu className="h-3 w-3 text-secondary" />
                          </span>
                          <span>{fieldDomain}</span>
                        </span>}
                      {technology && <span className="inline-flex items-center text-xs text-white/90 font-medium px-2 py-1 rounded-md bg-foreground/60 backdrop-blur-xs border border-white/10">
                          {technology}
                        </span>}
                    </div>

                    {/* Hover-reveal Engineering Process Overlay */}
                    {processSteps.length > 0 && <div className="absolute inset-0 z-20 bg-card/95 p-4 sm:p-5 flex flex-col justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-300 overflow-y-auto">
                        <div>
                          <div className="flex items-center gap-1.5 text-xs font-mono font-semibold uppercase tracking-wider text-secondary pb-2 mb-2.5 border-b border-border/80">
                            <Layers className="size-3.5 text-secondary" />
                            <span>{SECTION_CONTENT.engineeringProcess[lang]}</span>
                          </div>

                          <div className="space-y-2">
                            {processSteps.map(step => <div key={step.key} className="text-xs flex items-start min-w-0">
                                <span className="font-semibold text-primary font-mono inline-block mr-1.5 shrink-0">
                                  {step.label}:
                                </span>
                                <span className="text-muted-foreground line-clamp-2 min-w-0">
                                  {step.value}
                                </span>
                              </div>)}
                          </div>
                        </div>

                        <div className="pt-2 border-t border-border/60">
                          <Link to={projectLink} className="inline-flex items-center gap-1 text-xs font-semibold text-secondary hover:underline">
                            <span>{SECTION_CONTENT.specLink[lang]}</span>
                            <ArrowUpRight className="size-3" />
                          </Link>
                        </div>
                      </div>}
                  </div>

                  {/* Card Content */}
                  <CardHeader className="pt-5 pb-2 px-6">
                    <CardTitle className="font-serif text-xl font-bold tracking-tight text-card-foreground group-hover:text-primary transition-colors">
                      <Link to={projectLink} className="hover:underline">
                        {projectName}
                      </Link>
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="px-6 pb-6 pt-0 flex-1 flex flex-col justify-between min-w-0">
                    <div className="space-y-3 min-w-0">
                      {shortDesc && <CardDescription className="font-sans text-sm text-muted-foreground leading-relaxed line-clamp-2 min-w-0">
                          {shortDesc}
                        </CardDescription>}

                      {/* Compact Engineering Process Step Badges (Always visible on mobile/desktop) */}
                      {processSteps.length > 0 && <div className="pt-2">
                          <div className="flex flex-wrap items-center gap-1">
                            {processSteps.map(step => <span key={step.key} className="inline-flex items-center text-[10px] font-mono font-medium px-2 py-0.5 rounded-md bg-secondary/15 text-secondary-foreground border border-secondary/30">
                                {step.label}
                              </span>)}
                          </div>
                        </div>}

                      {/* Small Matching Program Link */}
                      {matchedProgram?.link && <div className="pt-1">
                          <Link to={matchedProgram.link} className="inline-flex items-center gap-1 text-xs font-semibold text-secondary hover:text-secondary/80 group/prog transition-colors">
                            <Sparkles className="size-3 text-secondary shrink-0" />
                            <span>{SECTION_CONTENT.relatedProgram[lang]}:</span>
                            <span className="underline underline-offset-2">
                              {matchedProgram.title}
                            </span>
                            <ArrowUpRight className="size-3 transition-transform group-hover/prog:translate-x-0.5 group-hover/prog:-translate-y-0.5" />
                          </Link>
                        </div>}
                    </div>

                    <div className="mt-5 pt-4 border-t border-border/80 flex items-center justify-between text-xs">
                      <span className="font-mono text-muted-foreground flex items-center gap-1.5">
                        <span className="p-0.5 rounded-sm bg-secondary/10">
                          <Wrench className="h-3.5 w-3.5 text-secondary" />
                        </span>
                        {SECTION_CONTENT.verifiedBadge[lang]}
                      </span>

                      <Link to={projectLink} className="inline-flex items-center gap-1 font-semibold text-primary group-hover:text-secondary transition-colors">
                        <span>{SECTION_CONTENT.specLink[lang]}</span>
                        <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
      })}
        </motion.div>}
    </div>;
}

// Consumer that receives programs and mounts the projects provider
function ProgramsProviderConsumer({
  lang
}: {
  lang: Language;
}) {
  const {
    posts: programs
  } = usePosts();
  return <PostsProvider wp_query={{
    post_type: "project",
    per_page: 20,
    status: ["publish"]
  }}>
      <ProjectsGalleryConsumer lang={lang} programs={programs} />
    </PostsProvider>;
}
export default function Gallery() {
  const [lang, setLang] = useState<Language>("vi");
  useEffect(() => {
    // Initial read from localStorage
    try {
      const stored = localStorage.getItem("roboto_lang");
      if (stored === "en" || stored === "vi") {
        setLang(stored);
      }
    } catch {
      // Ignore localStorage access issues
    }

    // Listen to custom window event
    const handleLangChange = (e: Event) => {
      const customEvent = e as CustomEvent<{
        lang: Language;
      }>;
      if (customEvent.detail?.lang === "vi" || customEvent.detail?.lang === "en") {
        setLang(customEvent.detail.lang);
      }
    };

    // Listen to native storage event for cross-tab sync
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "roboto_lang" && (e.newValue === "vi" || e.newValue === "en")) {
        setLang(e.newValue);
      }
    };
    window.addEventListener("roboto-lang-change", handleLangChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("roboto-lang-change", handleLangChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);
  return <section id="student-projects-gallery" data-nav="light" className="relative bg-background text-foreground py-20 md:py-24 border-b border-border overflow-hidden">
      {/* Decorative Lab Blueprint Coordinate Lines */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[linear-gradient(to_right,hsl(var(--foreground))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--foreground))_1px,transparent_1px)] bg-[size:40px_40px]" />

      <div className="relative mx-auto max-w-[1440px] px-6 sm:px-8 md:px-12 lg:px-16 xl:px-24">
        {/* Decorative Floating Mascot (Static, transparent, pointer-events-none, non-overlapping) */}
        <div aria-hidden="true" className="hidden lg:block absolute top-0 right-6 sm:right-8 md:right-12 lg:right-16 xl:right-24 z-10 pointer-events-none select-none">
          <img src={DEFAULT_MASCOT_SRC} alt="" className="w-24 h-24 lg:w-28 lg:h-28 xl:w-32 xl:h-32 object-contain bg-transparent drop-shadow-md pointer-events-none select-none" />
        </div>

        {/* Header Block with Signature Spec Rail Motif */}
        <div className="max-w-3xl mb-10 md:mb-12">
          {/* 1px Circuit-Cyan Spec Rail */}
          <div className="flex items-center gap-2 mb-4">
            <span className="h-1.5 w-1.5 rounded-xs bg-secondary" />
            <div className="h-[1px] w-12 bg-secondary" />
            <span className="font-mono text-xs uppercase tracking-widest text-secondary font-semibold">
              {SECTION_CONTENT.specRail[lang]}
            </span>
          </div>

          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
            {SECTION_CONTENT.heading[lang]}
          </h2>

          <p className="mt-4 font-sans text-lg md:text-xl text-muted-foreground leading-relaxed">
            {SECTION_CONTENT.subtitle[lang]}
          </p>

          <p className="mt-2 font-sans text-sm md:text-base text-muted-foreground/90 leading-relaxed">
            {SECTION_CONTENT.lead[lang]}
          </p>
        </div>

        {/* Live Programs & Projects Provider Pipeline */}
        <PostsProvider wp_query={{
        post_type: "program",
        per_page: 20,
        status: ["publish"]
      }}>
          <ProgramsProviderConsumer lang={lang} />
        </PostsProvider>

        {/* Closing Action Banner / Inquiry Strip */}
        <div className="mt-14 md:mt-16 p-6 sm:p-8 rounded-xl bg-muted/60 border border-border flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="p-1 rounded-md bg-accent/15">
                <Sparkles className="h-4 w-4 text-accent" />
              </span>
              <h3 className="font-serif font-bold text-lg text-foreground">
                {SECTION_CONTENT.ctaHeading[lang]}
              </h3>
            </div>
            <p className="font-sans text-sm text-muted-foreground">
              {SECTION_CONTENT.ctaDescription[lang]}
            </p>
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild className="w-full sm:w-auto bg-accent text-white hover:bg-accent/90 shadow-sm font-sans font-semibold px-6 py-3 h-auto">
              <Link to="/FreeTrialContactPage">{SECTION_CONTENT.ctaTrialBtn[lang]}</Link>
            </Button>
            <Button asChild variant="outline" className="w-full sm:w-auto font-sans font-medium px-5 py-3 h-auto border-border hover:bg-card">
              <Link to="/ProgramsPage">{SECTION_CONTENT.ctaProgramsBtn[lang]}</Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}